import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import { isPlainObject } from "es-toolkit/compat";
import { z } from "zod";
import {
  yearFormatter,
  yearMonthFormatter,
  yearMonthFromISO,
} from "./display.ts";
import { yearMonthParser } from "./parse.ts";
import { fromUTC } from "./utc.ts";

/**
 * Validates if a value is a string representing a year and month in the format 'yyyyMM'
 *
 * @example
 * isYearMonth('202301') // returns true (January 2023)
 * isYearMonth('202313') // returns false (invalid month)
 * isYearMonth('20230') // returns false (wrong length)
 * isYearMonth(202301) // returns false (not a string)
 *
 * @param value - The value to validate
 * @returns True if the value is a valid year-month string in yyyyMM format, false otherwise
 */
export const isYearMonth = (value: unknown): value is string => {
  if (
    typeof value !== "string" ||
    value.length !== 6 ||
    !/^\d{6}$/.test(value)
  ) {
    return false;
  }
  const month = parseInt(value.slice(4, 6), 10);
  return month >= 1 && month <= 12;
};

export const yearMonthToDate = (
  yyyymm: string
): { from: Date; to?: Date } | undefined => {
  if (!isYearMonth(yyyymm)) return undefined;

  return {
    from: parse(`${yyyymm}01`, "yyyyMMdd", new Date()),
  };
};

/** @deprecated consider using {@link DateTimeValueSchemas.dateTimeRangeStrict} or {@link DateTimeValueSchemas.dateTimeRange} independently instead */
export const isDateRangeValue = (
  value: unknown,
  parseFormat?: string
): value is DateTimeRangeStrictValueType => {
  if (!isPlainObject(value)) return false;

  const hasFrom =
    Object.hasOwn(value as object, "from") &&
    isValid(
      typeof (value as { from: Date | string }).from === "string"
        ? parseFormat
          ? parse((value as { from: string }).from, parseFormat, new Date())
          : parseISO((value as { from: string }).from)
        : (value as { from: Date }).from instanceof Date
          ? (value as { from: Date }).from
          : value
    );
  const hasTo =
    Object.hasOwn(value as object, "to") &&
    isValid(
      typeof (value as { to: Date | string }).to === "string"
        ? parseFormat
          ? parse((value as { to: string }).to, parseFormat, new Date())
          : parseISO((value as { to: string }).to)
        : (value as { to: Date }).to instanceof Date
          ? (value as { to: Date }).to
          : value
    );

  return hasFrom || (hasFrom && hasTo);
};

/**
 * Clamp a date between the provided floor date and the provided ceiling date
 * @return a clamped date between the provided floor and ceiling
 */
export function clampDate(
  dateToClamp: Date,
  opts: { floor?: Date; ceiling?: Date }
) {
  if (!dateToClamp) {
    return dateToClamp;
  }

  if (opts.floor && isBefore(dateToClamp, opts.floor)) {
    return opts.floor;
  }

  if (opts.ceiling && isAfter(dateToClamp, opts.ceiling)) {
    return opts.ceiling;
  }

  return dateToClamp;
}

/**
 * Clamp a year month date string (i.e. yyyyMM) between the smallest and largest
 * possible dates
 * @return a clamped year month date string
 */
export function clampYearMonth(yearMonth: string | undefined) {
  if (!yearMonth) {
    return yearMonth;
  }

  return yearMonthFormatter(
    clampDate(yearMonthParser(yearMonth), {
      floor: yearMonthParser("197001"),
      ceiling: yearMonthParser("999912"),
    })
  );
}

/**
 * Schemas that validate if a value is a particular kind of datetime
 */
export const DateTimeValueSchemas = {
  dateTime: z.string().datetime(),
  dateTimeStrict: z.date(),
  dateTimeRange: z.object({
    from: z.string().datetime(),
    to: z.string().datetime().optional(),
  }),
  dateTimeRangeStrict: z.object({
    from: z.date(),
    to: z.date().optional(),
  }),
  dateTimeRangeSimple: z.tuple([z.string().datetime(), z.string().datetime()]),
  dateYearMonth: z.string().regex(/^(\d{4})(\d{2})$/g),
  dateTimeToYearMonth: z
    .string()
    .datetime()
    .transform((value) => yearMonthFromISO(value)),
  dateTimeToYear: z
    .string()
    .datetime()
    .transform((value) =>
      yearFormatter(value ? fromUTC(parseISO(value)) : new Date())
    ),
};

export type DateTimeValueStrictType = z.infer<
  typeof DateTimeValueSchemas.dateTimeStrict
>;
export type DateTimeRangeValueType = z.infer<
  typeof DateTimeValueSchemas.dateTimeRange
>;
export type DateTimeRangeStrictValueType = z.infer<
  typeof DateTimeValueSchemas.dateTimeRangeStrict
>;
export type DateTimeRangeSimpleValueType = z.infer<
  typeof DateTimeValueSchemas.dateTimeRangeSimple
>;
